import { BasicProductItemVisibility } from "./BasicProductItemVisibility";
import { Button, H5, H6, CardHeader, CardContent } from "@sellar-io/components";
import { Tab } from "@headlessui/react";
import type {
  SupplierProductGetResponseChildType,
  SupplierProductGetResponseType,
} from "@sellar-io/api-v2/types";
import { useState, type ReactNode } from "react";
import {
  Icon,
  ItemTypeImage,
  StockIndicator,
  SpinnerLegacy,
} from "../../../../../../components";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
} from "../../../../../../components/dropdown/Dropdown";
import { Alert } from "../../../../../../components/alert/Alert";
import { AvailableFromBadge } from "../../../../../../components/badge/AvailableFromBadge";
import { BadgeZinc } from "../../../../../../components/badge/Badge";
import { HiddenBadge } from "../../../../../../components/badge/HiddenBadge";
import { MaxOrderQuantityBadge } from "../../../../../../components/badge/MaxOrderQuantityBadge";
import {
  InlineTab,
  InlineTabList,
} from "../../../../../../components/inlineTab/InlineTab";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../../../../hooks/api/v2/apiV2Request";
import { useProductDelete } from "../../../../../../hooks/api/v2/product/useProductDelete";
import { useProductUpdate } from "../../../../../../hooks/api/v2/product/useProductUpdate";
import ProductBadgeEditDialog from "../../../ProductBadgeEditDialog/ProductBadgeEditDialog";
import ItemDetailsDialog from "../../../ProductItemModal/ItemDetailsDialog";
import StockManagementDetailsDialog from "../../../StockManagementDetailsDialog/StockManagementDetailsDialog";
import { ItemVisibilityDialog } from "./ItemVisibilityDialog";
import { ProductCategoryItemDeliveryChargesTabContent } from "./ProductCategoryItemDeliveryChargesTabContent/ProductCategoryItemDeliveryChargesTabContent";
import { ProductCategoryItemPriceTabContent } from "./ProductCategoryItemPriceTab/ProductCategoryItemPriceTabContent";
import { ProductCategoryItemStockTabContent } from "./ProductCategoryItemStockTabContent";
import { useProductCatalogueMode } from "~/hooks/useProductCatalogueMode";
import { useRemoteFeatureFlags } from "~/hooks/useRemoteFeatureFlags";

type ProductCategoryItemProps = {
  product: SupplierProductGetResponseChildType;
  parentProduct: SupplierProductGetResponseType;
};

export const ProductCategoryItemV2 = (props: ProductCategoryItemProps) => {
  const productCatalogueMode = useProductCatalogueMode();
  if (productCatalogueMode.isLoading) {
    return <SpinnerLegacy />;
  }
  return productCatalogueMode.isBasicMode ? (
    <BasicProductCategoryItem {...props} />
  ) : (
    <AdvancedProductCategoryItem {...props} />
  );
};

const BasicProductCategoryItem = (props: ProductCategoryItemProps) => (
  <ProductCategoryItemCard
    {...props}
    visibilityControl={
      <BasicProductItemVisibility
        product={props.product}
        parentActive={props.parentProduct.active}
      />
    }
  />
);

const AdvancedProductCategoryItem = (props: ProductCategoryItemProps) => {
  const { product, parentProduct } = props;
  const [itemVisibilityDialogActive, setItemVisibilityDialogActive] =
    useState(false);
  return (
    <>
      <ProductCategoryItemCard
        {...props}
        visibilityMenuItem={
          <DropdownItem onClick={() => setItemVisibilityDialogActive(true)}>
            Item visibility
          </DropdownItem>
        }
        visibilityBadge={
          product._count.activeProductAvailabilityTiers > 0 &&
          product.active &&
          parentProduct.active && (
            <BadgeZinc icon="EYE">
              <span className="font-normal">
                <span className="font-semibold">Visible</span> on{" "}
                {product._count.activeProductAvailabilityTiers}/
                {product._count.totalAvailabilityTiers} product catalogues
              </span>
            </BadgeZinc>
          )
        }
      />
      <ItemVisibilityDialog
        product={product}
        active={itemVisibilityDialogActive}
        onDismiss={() => setItemVisibilityDialogActive(false)}
      />
    </>
  );
};

const ProductCategoryItemCard = ({
  product,
  parentProduct,
  visibilityControl,
  visibilityMenuItem,
  visibilityBadge,
}: ProductCategoryItemProps & {
  visibilityControl?: ReactNode;
  visibilityMenuItem?: ReactNode;
  visibilityBadge?: ReactNode;
}) => {
  const productUpdateMutation = useProductUpdate(product.id);
  const productDeleteMutation = useProductDelete(product.id);
  const productItemName = product.name?.includes(" • ")
    ? product.name.split(" • ").slice(1).join(" • ")
    : product.name;

  const unbundlingEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingReleaseFinal",
  );

  const [
    stockManagementDetailsDialogActive,
    setStockManagementDetailsDialogActive,
  ] = useState(false);
  const [itemDetailsDialogActive, setItemDetailsDialogActive] = useState(false);
  const [deleteDialogActive, setDeleteDialogActive] = useState(false);

  const onDismiss = () => setDeleteDialogActive(false);
  const [updateBadgeTypeActive, setUpdateBadgeTypeActive] = useState(false);

  return (
    <div>
      <CardHeader className="min-h-0 bg-white p-0">
        <div className={"flex items-center justify-between p-4"}>
          <div className={"flex items-center gap-2"}>
            <ItemTypeImage
              product={{
                containerType: product.containerType ?? null,
                type: product.type,
              }}
            />
            <div>
              <H5>{productItemName}</H5>
              {product.sku && <H6>SKU: {product.sku}</H6>}
            </div>
          </div>
          <div className={"flex items-center gap-2"}>
            <Dropdown>
              <DropdownButton>
                <Button size={"small"} color={"transparent"}>
                  <Icon icon={"DOTS_VERTICAL"} />
                </Button>
              </DropdownButton>
              <DropdownItems>
                <DropdownItem
                  onClick={() =>
                    product.type === "badge"
                      ? setUpdateBadgeTypeActive(true)
                      : setItemDetailsDialogActive(true)
                  }
                >
                  Item details
                </DropdownItem>
                <DropdownItem
                  onClick={() => setStockManagementDetailsDialogActive(true)}
                >
                  Stock management
                </DropdownItem>
                {visibilityMenuItem}
                <DropdownItem
                  className={"text-red-500"}
                  onClick={() => setDeleteDialogActive(true)}
                >
                  Delete item
                </DropdownItem>
              </DropdownItems>
            </Dropdown>
          </div>
        </div>
      </CardHeader>
      <CardContent className="rounded-b-lg bg-white p-4">
        <div className="flex flex-row justify-between">
          <div className="flex w-full flex-col gap-4">
            <div className="flex flex-wrap items-center gap-1">
              {product.active &&
              parentProduct.active &&
              product._count.activeProductAvailabilityTiers > 0 ? (
                <StockIndicator
                  stock={product.availableStock}
                  canBackorder={product.canBackorder}
                />
              ) : (
                <HiddenBadge />
              )}
              {visibilityBadge}
              {!!product.maxOrderQuantity && (
                <MaxOrderQuantityBadge maxQuantity={product.maxOrderQuantity} />
              )}
              {!!product.availableFrom && (
                <AvailableFromBadge availableFrom={product.availableFrom} />
              )}
            </div>
            {visibilityControl}
            <Tab.Group>
              <div className="flex flex-row items-center justify-between gap-2">
                <InlineTabList>
                  <InlineTab>Stock</InlineTab>
                  <InlineTab className={"flex gap-2"}>
                    {product._count.ProductPriceTiers === 0 && (
                      <Icon icon={"ALERT"} className={"text-orange-500"} />
                    )}{" "}
                    Pricing
                  </InlineTab>
                  {!unbundlingEnabled && <InlineTab>Delivery</InlineTab>}
                </InlineTabList>
              </div>
              <Tab.Panels>
                <Tab.Panel className={"flex flex-col gap-2 pt-4"}>
                  <ProductCategoryItemStockTabContent
                    product={product}
                    onClick={() => setStockManagementDetailsDialogActive(true)}
                  />
                </Tab.Panel>
                <Tab.Panel>
                  <ProductCategoryItemPriceTabContent product={product} />
                </Tab.Panel>
                {!unbundlingEnabled && (
                  <Tab.Panel>
                    <ProductCategoryItemDeliveryChargesTabContent
                      product={product}
                    />
                  </Tab.Panel>
                )}
              </Tab.Panels>
            </Tab.Group>
          </div>
        </div>
      </CardContent>

      <StockManagementDetailsDialog
        active={stockManagementDetailsDialogActive}
        onDismiss={() => setStockManagementDetailsDialogActive(false)}
        product={product}
      />

      <ItemDetailsDialog
        active={itemDetailsDialogActive}
        onDismiss={() => setItemDetailsDialogActive(false)}
        product={{ ...product, Parent: parentProduct }}
      />

      <ProductBadgeEditDialog
        active={updateBadgeTypeActive}
        onDismiss={() => setUpdateBadgeTypeActive(false)}
        product={product}
        loading={productUpdateMutation.isPending}
        onUpdate={(updatedProduct) =>
          productUpdateMutation.mutate(
            { sku: updatedProduct.sku },
            {
              onSuccess: () => {
                setUpdateBadgeTypeActive(false);
              },
            },
          )
        }
      />

      <Dialog active={deleteDialogActive} onDismiss={onDismiss}>
        <DialogNavigation
          onClickLeftIcon={onDismiss}
          leftIcon={"CHEVRON_DOWN"}
          title={`Delete ${product.name}?`}
          description={"Are you sure you wish to delete this item?"}
        />
        <DialogContent className={"p-4"}>
          {productDeleteMutation.isError && (
            <Alert
              type={"error"}
              message={
                apiV2AssertError(productDeleteMutation.error)?.response?.data
                  .message ?? "Unknown error"
              }
            />
          )}
        </DialogContent>
        <DialogCTA>
          <Button
            loading={productDeleteMutation.isPending}
            color={"red"}
            onClick={() =>
              productDeleteMutation.mutate(undefined, {
                onSuccess: () => {
                  onDismiss();
                },
              })
            }
          >
            Delete
          </Button>
        </DialogCTA>
      </Dialog>
    </div>
  );
};
